import { pluralizeWithCount } from "@common/helpers/string";
import { NestedEditorRow } from "./nestedRowTypes";

// One display row's worth of delete: its own transaction on the workspace host,
// carrying the businesses the confirmation has to name.
export interface DeleteRowUnit {
    kind: "account" | "group";
    ids: string[];
    entityIds: number[];
    depth: number;
    // The row's display name, for the account-row confirmation copy.
    name: string;
}

export const toDeleteRowUnit = (row: NestedEditorRow): DeleteRowUnit | null => {
    if (row.kind === "account") {
        return {
            kind: "account",
            ids: row.accounts.map((account) => account.id),
            entityIds: row.accounts.map((account) => account.entityId),
            depth: row.depth,
            name: row.name,
        };
    }
    if (row.kind === "group") {
        return {
            kind: "group",
            ids: row.groups.map((group) => group.id),
            entityIds: row.groups.map((group) => group.entityId),
            depth: row.depth,
            name: row.name,
        };
    }
    return null;
};

// Accounts first, then groups deepest-first: a selected child group's
// pre-gesture name path has to still resolve once its selected parent is gone.
export const orderDeleteUnits = (units: DeleteRowUnit[]): DeleteRowUnit[] =>
    [...units].sort((left, right) => {
        if (left.kind !== right.kind) {
            return left.kind === "account" ? -1 : 1;
        }
        if (left.kind === "account") {
            return 0;
        }
        return right.depth - left.depth;
    });

export const uniqueEntityIds = (units: DeleteRowUnit[]): number[] => [
    ...new Set(units.flatMap((unit) => unit.entityIds)),
];

export interface NestedDeleteCopy {
    title: string;
    question: string;
}

// Workspace copy counts businesses, not constituents: two rows collapsed onto
// one line in one business are one business, not two.
export const resolveWorkspaceDeleteCopy = ({
    kind,
    businessNames,
    name,
}: {
    kind: "account" | "group" | "bulk";
    businessNames: string[];
    // The account row's display name; only the account copy names it.
    name?: string;
}): NestedDeleteCopy => {
    const reach = `${pluralizeWithCount(
        "business",
        businessNames.length,
    )}: ${businessNames.join(", ")}`;
    if (kind === "group") {
        return {
            title: "Delete group?",
            question: `This removes only the group. Its accounts and subgroups move up one level. This reaches ${reach}.`,
        };
    }
    if (kind === "account") {
        return {
            title: "Delete account?",
            question: `This will remove ${name ?? "this account"} from the chart in ${reach}.`,
        };
    }
    return {
        title: "Delete from the chart?",
        question: `This will remove the selected rows from the chart in ${reach}.`,
    };
};
